import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { planningService } from '../services/api';
import { RendezVous, PaginatedResponse } from '../types';

interface UseRendezVousOptions {
  dateRange: {
    start: string;
    end: string;
  };
  statusFilter?: string;
}

export function useRendezVous({ dateRange, statusFilter = 'all' }: UseRendezVousOptions) {
  const queryClient = useQueryClient();

  // Requête pour récupérer les rendez-vous
  const {
    data: rdvData,
    isLoading,
    error
  } = useQuery<PaginatedResponse<RendezVous>>({
    queryKey: ['rendez-vous', { date_debut: dateRange.start, date_fin: dateRange.end }],
    queryFn: () => planningService.getRendezVous({
      date_debut: dateRange.start,
      date_fin: dateRange.end,
      page_size: 1000
    }).then(res => res.data)
  });

  const rendezVous = rdvData?.results || [];

  // Filtrer les rendez-vous selon le statut
  const filteredRendezVous = statusFilter === 'all' 
    ? rendezVous 
    : rendezVous.filter(rdv => rdv.status === statusFilter);

  // Mutations pour les actions sur les rendez-vous
  const confirmerMutation = useMutation({
    mutationFn: (id: number) => planningService.confirmerRdv(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] });
    }
  });

  const annulerMutation = useMutation({
    mutationFn: (id: number) => planningService.annulerRdv(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] });
    }
  });

  const changerStatutMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => 
      planningService.changerStatutRdv(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['rendez-vous'] });
    }
  });

  // Actions
  const confirmerRdv = (id: number) => confirmerMutation.mutate(id);
  const annulerRdv = (id: number) => annulerMutation.mutate(id);
  const changerStatutRdv = (id: number, status: string) => changerStatutMutation.mutate({ id, status });

  return {
    // Données
    rendezVous,
    filteredRendezVous,
    isLoading,
    error,
    
    // Actions
    confirmerRdv,
    annulerRdv,
    changerStatutRdv,
    
    // États des mutations
    isConfirming: confirmerMutation.isLoading,
    isCancelling: annulerMutation.isLoading,
    isChangingStatus: changerStatutMutation.isLoading,
    
    // Invalidation manuelle du cache
    invalidateRendezVous: () => queryClient.invalidateQueries({ queryKey: ['rendez-vous'] })
  };
}